import React, { useEffect, useState } from "react";
import { X, Mail, MapPin, Sparkles, Cpu, MoveRight, Check } from "lucide-react";
import { useDemoModal } from "../hooks/useDemoModal";

const INDUSTRIES = [
    "Select…",
    "Services / Agency",
    "Software / SaaS",
    "Manufacturing",
    "Retail / E-commerce",
    "Hospitality / F&B",
    "Healthcare",
    "Education",
    "Real estate / Construction",
    "Logistics / Trading",
    "Chartered Accountant",
    "Other",
];

const BENEFITS = [
    { Icon: Sparkles, title: "Tailored walkthrough",   body: "We'll focus on the modules relevant to your business and team size." },
    { Icon: Cpu,      title: "Live AI Copilot demo",   body: "See Copilot answer real questions using data like yours." },
    { Icon: MoveRight,title: "Migration plan",         body: "Walk away with a clear plan to bring your existing books over." },
];

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function BookDemoModal() {
    const { open, closeModal } = useDemoModal();
    const [form, setForm] = useState({ full_name: "", company: "", work_email: "", phone: "", industry: "", message: "" });
    const [state, setState] = useState({ loading: false, error: null, done: null });

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") closeModal(); };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    }, [open, closeModal]);

    useEffect(() => {
        if (!open) {
            setState({ loading: false, error: null, done: null });
            setForm({ full_name: "", company: "", work_email: "", phone: "", industry: "", message: "" });
        }
    }, [open]);

    if (!open) return null;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setState({ loading: true, error: null, done: null });
        try {
            const res = await fetch(`${API_BASE}/api/leads/demo-request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) {
                const detail = Array.isArray(data.detail) ? data.detail.map((d) => d.msg || d.type).join(" · ") : (data.detail || "Something went wrong");
                throw new Error(detail);
            }
            setState({ loading: false, error: null, done: data });
        } catch (err) {
            setState({ loading: false, error: err.message || "Network error", done: null });
        }
    };

    return (
        <div
            role="dialog" aria-modal="true" aria-label="Book a demo"
            onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
            data-testid="book-demo-modal"
            style={{
                position: "fixed", inset: 0, zIndex: 100, background: "rgba(13,18,38,0.72)",
                backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 24, overflowY: "auto",
            }}
        >
            <div style={{
                background: "#13192e", color: "#f6f2eb",
                border: "1px solid rgba(166,138,86,0.35)", borderRadius: 2,
                width: "100%", maxWidth: 960, boxShadow: "0 40px 80px -30px rgba(0,0,0,0.6)",
                position: "relative",
            }}>
                <button
                    type="button" onClick={closeModal} data-testid="modal-close"
                    style={{
                        position: "absolute", top: 14, right: 14, background: "transparent",
                        border: "1px solid rgba(246,242,235,0.2)", color: "rgba(246,242,235,0.7)",
                        borderRadius: 2, width: 32, height: 32, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                    aria-label="Close"
                ><X size={16} /></button>

                {state.done ? (
                    <div style={{ padding: 56, textAlign: "center" }} data-testid="modal-success">
                        <div style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 56, height: 56, borderRadius: "50%",
                            background: "rgba(94,234,212,0.14)", border: "1px solid rgba(94,234,212,0.4)",
                        }}><Check size={26} color="#5eead4" /></div>
                        <div style={{ fontFamily: "IBM Plex Mono,monospace", fontSize: 10.5, letterSpacing: "0.18em", color: "#5eead4", marginTop: 18, textTransform: "uppercase", fontWeight: 700 }}>DEMO REQUEST RECEIVED</div>
                        <h2 style={{ fontFamily: "Newsreader,Georgia,serif", fontSize: "2rem", margin: "12px 0 0 0", color: "#f6f2eb", fontStyle: "italic" }}>Thanks — we&apos;ve got it.</h2>
                        <p style={{ marginTop: 14, color: "rgba(246,242,235,0.7)", fontSize: 15, maxWidth: 460, marginLeft: "auto", marginRight: "auto", lineHeight: 1.55 }}>
                            A member of the team will be in touch within one business day to schedule your 30-minute walkthrough.
                        </p>
                        <div style={{ marginTop: 18, fontFamily: "IBM Plex Mono,monospace", fontSize: 10.5, color: "rgba(246,242,235,0.4)" }}>
                            Ref: {state.done.lead_id?.slice(0, 8)}
                        </div>
                        <button
                            type="button" onClick={closeModal} data-testid="modal-success-close"
                            style={{
                                marginTop: 28, padding: "12px 22px", background: "#a68a56", color: "#13192e",
                                border: 0, borderRadius: 999, fontSize: 13.5, fontWeight: 600, cursor: "pointer",
                            }}
                        >Close</button>
                    </div>
                ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "60fr 40fr" }} className="vy-grid-2">
                        {/* LEFT: form */}
                        <form onSubmit={submit} style={{ padding: "36px 32px", borderRight: "1px solid rgba(166,138,86,0.2)" }} data-testid="demo-form">
                            <div style={{ fontFamily: "IBM Plex Mono,monospace", fontSize: 10.5, letterSpacing: "0.18em", color: "#a68a56", textTransform: "uppercase", fontWeight: 700 }}>
                                &gt; book.demo --duration=30m
                            </div>
                            <h2 style={{ fontFamily: "Newsreader,Georgia,serif", fontSize: "1.9rem", margin: "10px 0 4px 0", lineHeight: 1.1 }}>
                                Book a demo.
                            </h2>
                            <p style={{ color: "rgba(246,242,235,0.65)", fontSize: 13.5, marginBottom: 22 }}>
                                Tell us a bit about your business. We&apos;ll follow up within one business day.
                            </p>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="vy-grid-2">
                                <FieldLabel label="Full name" required>
                                    <Input value={form.full_name} onChange={set("full_name")} required name="full_name" data-testid="field-full-name" />
                                </FieldLabel>
                                <FieldLabel label="Company" required>
                                    <Input value={form.company} onChange={set("company")} required name="company" data-testid="field-company" />
                                </FieldLabel>
                                <FieldLabel label="Work email" required>
                                    <Input type="email" value={form.work_email} onChange={set("work_email")} required name="work_email" data-testid="field-email" />
                                </FieldLabel>
                                <FieldLabel label="Phone (optional)">
                                    <Input type="tel" value={form.phone} onChange={set("phone")} name="phone" data-testid="field-phone" />
                                </FieldLabel>
                            </div>

                            <div style={{ marginTop: 12 }}>
                                <FieldLabel label="Industry" required>
                                    <select
                                        value={form.industry} onChange={set("industry")} required
                                        data-testid="field-industry"
                                        style={inputStyle}
                                    >
                                        {INDUSTRIES.map((x) => (
                                            <option key={x} value={x === "Select…" ? "" : x} disabled={x === "Select…"}>{x}</option>
                                        ))}
                                    </select>
                                </FieldLabel>
                            </div>

                            <div style={{ marginTop: 12 }}>
                                <FieldLabel label="What are you trying to solve right now?" required>
                                    <textarea
                                        value={form.message} onChange={set("message")} required rows={4}
                                        data-testid="field-message"
                                        style={{ ...inputStyle, resize: "vertical", minHeight: 96, fontFamily: "inherit" }}
                                    />
                                </FieldLabel>
                            </div>

                            {state.error && (
                                <div data-testid="form-error" style={{ marginTop: 14, padding: "10px 12px", background: "rgba(220,38,38,0.15)", border: "1px solid rgba(220,38,38,0.4)", borderRadius: 2, color: "#fca5a5", fontSize: 13 }}>
                                    {state.error}
                                </div>
                            )}

                            <button
                                type="submit" disabled={state.loading} data-testid="form-submit"
                                style={{
                                    marginTop: 22, width: "100%", padding: "14px 22px",
                                    background: state.loading ? "rgba(166,138,86,0.5)" : "#a68a56",
                                    color: "#13192e", border: 0, borderRadius: 999,
                                    fontSize: 14, fontWeight: 700, cursor: state.loading ? "wait" : "pointer",
                                    letterSpacing: 0.02, transition: "background 180ms",
                                }}
                            >
                                {state.loading ? "Sending…" : "Book a demo →"}
                            </button>
                            <p style={{ marginTop: 12, fontSize: 11, color: "rgba(246,242,235,0.4)", fontFamily: "IBM Plex Mono,monospace" }}>
                                * We&apos;ll only use these details to schedule your walkthrough.
                            </p>
                        </form>

                        {/* RIGHT: info panel */}
                        <aside style={{ padding: "36px 32px", background: "rgba(246,242,235,0.02)" }}>
                            <span style={{
                                display: "inline-block", padding: "4px 10px", borderRadius: 999,
                                background: "rgba(94,234,212,0.12)", color: "#5eead4",
                                fontFamily: "IBM Plex Mono,monospace", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700,
                                border: "1px solid rgba(94,234,212,0.3)",
                            }}>Why book a demo?</span>
                            <h3 style={{ fontFamily: "Newsreader,Georgia,serif", fontSize: "1.6rem", lineHeight: 1.15, margin: "16px 0 0 0", color: "#f6f2eb" }}>
                                30 minutes.<br />Real numbers.<br /><em style={{ color: "#a68a56", fontWeight: 400 }}>No fluff.</em>
                            </h3>

                            <ul style={{ listStyle: "none", padding: 0, margin: "26px 0 0 0", display: "grid", gap: 18 }}>
                                {BENEFITS.map(({ Icon, title, body }) => (
                                    <li key={title} style={{ display: "flex", gap: 14 }}>
                                        <div style={{
                                            flexShrink: 0, width: 32, height: 32, borderRadius: 2,
                                            background: "rgba(166,138,86,0.14)", border: "1px solid rgba(166,138,86,0.35)",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            <Icon size={15} color="#a68a56" strokeWidth={1.8} />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 13.5, color: "#f6f2eb", marginBottom: 2 }}>{title}</div>
                                            <div style={{ fontSize: 12.5, color: "rgba(246,242,235,0.65)", lineHeight: 1.5 }}>{body}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            <div style={{ marginTop: 30, paddingTop: 20, borderTop: "1px dashed rgba(166,138,86,0.3)" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: "rgba(246,242,235,0.75)", fontFamily: "IBM Plex Mono,monospace" }}>
                                    <Mail size={13} color="#a68a56" strokeWidth={1.8} /> hello@scitus.ai
                                </div>
                                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: "rgba(246,242,235,0.75)", fontFamily: "IBM Plex Mono,monospace" }}>
                                    <MapPin size={13} color="#a68a56" strokeWidth={1.8} /> Available across India
                                </div>
                            </div>
                        </aside>
                    </div>
                )}
            </div>
        </div>
    );
}

const inputStyle = {
    width: "100%", padding: "10px 12px", background: "#f6f2eb", color: "#13192e",
    border: "1px solid rgba(166,138,86,0.3)", borderRadius: 2, fontSize: 13.5, outline: "none",
    fontFamily: "inherit", transition: "border-color 160ms, box-shadow 160ms",
};

function Input(props) {
    return (
        <input
            {...props}
            style={inputStyle}
            onFocus={(e) => { e.target.style.borderColor = "#a68a56"; e.target.style.boxShadow = "0 0 0 3px rgba(166,138,86,0.18)"; }}
            onBlur={(e) => { e.target.style.borderColor = "rgba(166,138,86,0.3)"; e.target.style.boxShadow = "none"; }}
        />
    );
}

function FieldLabel({ label, required, children }) {
    return (
        <label style={{ display: "block" }}>
            <span style={{ display: "block", fontFamily: "IBM Plex Mono,monospace", fontSize: 10, letterSpacing: "0.14em", color: "rgba(246,242,235,0.7)", textTransform: "uppercase", marginBottom: 6 }}>
                {label}{required && <span style={{ color: "#a68a56" }}> *</span>}
            </span>
            {children}
        </label>
    );
}
